import React from 'react';
import { COLORS, FONTS } from '../../styles/tokens';

/** Shared colored pill for app status. */
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  running: { bg: 'rgba(74,222,128,0.15)', text: '#4ADE80' },
  built: { bg: 'rgba(74,222,128,0.15)', text: '#4ADE80' },
  building: { bg: 'rgba(251,191,36,0.15)', text: '#FBBF24' },
  deploying: { bg: 'rgba(251,191,36,0.15)', text: '#FBBF24' },
  pending: { bg: 'rgba(107,114,128,0.15)', text: '#6B7280' },
  stopped: { bg: 'rgba(107,114,128,0.15)', text: '#6B7280' },
  build_failed: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
  deploy_failed: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
  deleted: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
};

interface InfoCardProps {
  title: string;
  isDark: boolean;
  colors: { border: string; card?: string; textSecondary: string };
  children: React.ReactNode;
}

/** Labelled container used to group related status fields on the app detail page. */
export function InfoCard({ title, isDark, colors, children }: InfoCardProps): React.JSX.Element {
  return (
    <div className="vx-fill-col" style={{
      flex: '1 1 280px', minWidth: '260px', padding: '16px', borderRadius: '10px',
      border: `1px solid ${colors.border}`,
      backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
    }}>
      <h3 style={{
        fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: colors.textSecondary, margin: '0 0 12px',
      }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {children}
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
  colors: { textSecondary: string; text: string };
  mono?: boolean;
}

/** One labelled value row inside an InfoCard. */
export function InfoRow({ label, value, colors, mono }: InfoRowProps): React.JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
      <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, flexShrink: 0 }}>{label}</span>
      <span className="vx-info-value" style={{
        fontFamily: mono ? 'monospace' : FONTS.body, fontSize: '11px',
        color: colors.text, textAlign: 'right', wordBreak: 'break-all', maxWidth: '200px',
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {value}
      </span>
    </div>
  );
}

/** Colored pill — lookup-driven styling keyed by the `deployed_apps.*_status` enums. */
export function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const { bg, text } = STATUS_COLORS[status] ?? STATUS_COLORS.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '2px 10px', borderRadius: '12px',
      backgroundColor: bg, fontFamily: FONTS.body, fontSize: '11px', fontWeight: 600, color: text,
      alignSelf: 'flex-start',
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: text }} />
      {status.replace('_', ' ')}
    </span>
  );
}
